import React from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {Space} from 'antd';
import {StyledBudgetInvoicesTable} from './StyledBudgetInvoicesTable';
import UnlinkBudgetButton from '../UnlinkBudgetButton';
import IconWrapper from '../../../IconWrapper';
import Table from '../../../Table';
import InvoicePreviewWithIcon from '../../NetSuiteJournalEntriesPage/InvoicePreviewWithIcon';
import {Invoice2Icon} from '../../../../icons';
import {amountHelpers, dateHelpers, invoicesHelpers} from '../../../../utils/helpers';

const dateColumnProps = {
  render: (value) => value ? dateHelpers.getDateWithMonth(value) : '',
  width: 120,
};

const actionsClassName = 'budget-invoices-table-actions';

const BudgetInvoicesTable = ({
  data = [],
  loading = false,
  onUnlink,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const navigate = useNavigate();

  const columns = [
    {
      dataIndex: 'date',
      title: `${t('invoice')} ${t('date')}`,
      ...dateColumnProps
    },
    {
      dataIndex: 'vendor_local_name',
      render: (_, record) => invoicesHelpers.getInvoiceVendorName(record),
      title: t('vendor')
    },
    {
      dataIndex: 'approval_status',
      render: (_, record) => invoicesHelpers.getInvoiceApprovalStatusTag(record, t),
      sorter: true,
      title: t('status')
    },
    {
      dataIndex: 'uploaded_by',
      title: t('user')
    },
    {
      align: 'right',
      dataIndex: 'amount',
      render: (amount, record) => (
        <span className='amount'>
          {amount ? amountHelpers.getAmountWithCurrencyCode(amount, undefined, record?.currency) : ''}
        </span>
      ),
      title: t('amount'),
    },
    {
      dataIndex: 'actions',
      render: (_, record) => (
        <Space direction='horizontal' size='small' className={`d-flex ${actionsClassName}`}>
          <InvoicePreviewWithIcon
            invoiceId={record.id}
            icon={<IconWrapper size={16}><Invoice2Icon /></IconWrapper>}
          />
          <UnlinkBudgetButton onOk={() => onUnlink?.(record)} />
        </Space>
      ),
      title: '',
      width: 64
    }
  ];

  const handleOnRowClick = (e, record) => {
    if (e.target.closest('.ant-modal-root') || e.target.closest(`.${actionsClassName}`)) return;

    const isUnassigned = invoicesHelpers.isUnassignedInvoice(record);
    const url = isUnassigned
      ? invoicesHelpers.getInvoiceDetailsLink(record.id)
      : invoicesHelpers.getInvoicePaymentDetailsLink(record.id);

    navigate(url);
  }

  return (
    <StyledBudgetInvoicesTable
      {...rest}
      size='large'
      direction='vertical'
    >
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        onRow={(record) => ({
          onClick: (e) => handleOnRowClick(e, record)
        })}
        rowKey='id'
        pagination={false}
      />
    </StyledBudgetInvoicesTable>
  );
}

BudgetInvoicesTable.propTypes = {
  data: Table.propTypes.dataSource,
  loading: PropTypes.bool,
  onUnlink: PropTypes.func,
}

export default BudgetInvoicesTable;
